/**
 * constants.js — every tunable number, Firestore name and storage key of Daily Ops.
 *
 * Data that describes the lists lives next door: config/lists.js (built-in lists +
 * statuses), config/checklists.js (Shopping / Backup / Cutting), config/closingSeed.js
 * (the printed closing sheets). App-wide constants (store hours…) live in src/constants.js.
 */

// ── Firestore collections ────────────────────────────────────────────────────
export const ITEMS_COLLECTION = 'dailyRestockItems';            // one doc per noted board item
export const LISTS_COLLECTION = 'opsLists';                     // lists made / restyled in the app
export const ROUTES_DOC_ID = '_routes';                         // opsLists doc: which list each name lives in (+ names removed from a list)
export const DELETED_DOC_ID = '_deleted';                       // opsLists doc: tombstones of deleted checklist-history rows
export const CHECKLIST_COLLECTION = 'opsChecklistItems';        // standing checklist items
export const CHECKLIST_LOG_COLLECTION = 'opsChecklistLog';      // one row per completed checklist cycle
export const CLOSING_SECTIONS_COLLECTION = 'opsClosingSections';
export const CLOSING_SUBSECTIONS_COLLECTION = 'opsClosingSubsections';
export const CLOSING_ITEMS_COLLECTION = 'opsClosingItems';
export const TRANSLATIONS_COLLECTION = 'opsTranslations';       // shared English ⇄ Telugu names

// ── Query caps (a ceiling, not an expectation) ───────────────────────────────
export const RECENT_QUERY_LIMIT = 1500;     // the live board window
export const HISTORY_FETCH_LIMIT = 3000;    // settled history / an on-demand older-history fetch
export const LOG_HISTORY_LIMIT = 5000;      // checklist completion log kept on a device (newest first)

// ── Time windows ─────────────────────────────────────────────────────────────
export const MS_PER_DAY = 86_400_000;
export const HISTORY_WINDOW_DAYS = 60;      // how far back Today/History/suggestions load
export const HISTORY_RANGE_OPTIONS = [7, 14, 30, 60, 90];   // >60 days is fetched on demand (one-shot)
export const DEFAULT_HISTORY_RANGE = 14;
// Of those 60 days only the last LIVE_WINDOW_DAYS are listened to on the server (plus
// any older item still on the open list). Older days are "settled": served from the
// device's Firestore cache and re-fetched from the server once SETTLED_REFRESH_MS has
// passed. Keep SETTLED_REFRESH_MS under LIVE_WINDOW_DAYS: then anything noted since
// the last refresh is still inside the live window, so no item can go missing — only
// another device's later edit to an old item can show up to SETTLED_REFRESH_MS late.
// See subscribeRecentItems (data/items.js) for why.
export const LIVE_WINDOW_DAYS = 7;
export const SETTLED_REFRESH_MS = 2 * MS_PER_DAY;
// Audit / Summary ranges. 0 = "All time" (no lower bound); custom from/to overrides these.
export const AUDIT_RANGE_OPTIONS = [7, 30, 90, 180, 0];
export const DEFAULT_AUDIT_RANGE = 30;

// ── The open list (list cycles) ──────────────────────────────────────────────
// The Today board is ONE open list that stays until someone moves it to History (no
// midnight wipe). A new list "cycle" starts at closing time: from this hour (24h) on,
// new notes are for TOMORROW's list, and the first add of a new cycle asks whether to
// move the open list to History or keep adding to it. Closing past midnight stays in
// the same cycle (Fri 9 PM and Sat 12:30 AM are both "the list for Saturday").
export const LIST_CYCLE_HOUR = 20;
// Items saved before list cycles existed (no `listFor`) count as OPEN from this date on
// (the last list noted under the old midnight rule), so nothing vanishes at switch-over;
// older ones are already history. Doesn't need changing when you deploy.
export const LIST_CYCLES_SINCE = '2026-09-22';
// In-app closing reminder: nudge to note tomorrow's needs after this hour (24h).
export const CLOSING_REMINDER_HOUR = LIST_CYCLE_HOUR;
export const ORDER_DAYS = [2, 5];           // Tue, Fri (Date.getDay()) — Shopping-list days

// ── UI limits & timings ──────────────────────────────────────────────────────
export const TOP_NEEDED_COUNT = 5;          // History "most needed" pills
export const TOAST_MS = 2_800;
export const TOAST_UNDO_MS = 6_000;
export const AUDIT_TOP_STORES = 8;          // rows in the Summary view's store breakdown
export const REORDER_SUGGEST_COUNT = 8;     // most-overdue items offered in the "due to reorder" strip
export const USUAL_COUNT = 8;               // items "✨ Add my usual" puts back on an empty list
export const MAX_ELSEWHERE = 6;             // matches from OTHER lists offered while searching a panel

// ── Touch / swipe gestures ───────────────────────────────────────────────────
// Touch-first: any coarse-pointer device (iPad, phone) gets the tap-to-complete /
// swipe-to-delete / bottom-sheet row — not just narrow phones. Desktops (fine
// pointer / mouse) keep the compact click row.
export const TOUCH_MEDIA_QUERY = '(pointer: coarse)';
export const SWIPE_MAX_PX = 100;             // furthest the row travels while dragging
export const SWIPE_DELETE_THRESHOLD_PX = 68; // release past this → delete
export const SWIPE_LOCK_PX = 8;              // movement before we lock the h/v direction
export const SWIPE_ANIM_MS = 180;            // slide-off animation before the delete fires
export const SWIPE_SETTLE_MS = 220;          // snap-back after a swipe that didn't reach delete

// ── localStorage keys (per device) ───────────────────────────────────────────
export const USER_NAME_LS_KEY = 'db_user_name';                    // set by AuthContext; fallback author name
export const LANG_LS_KEY = 'do_lang';                              // English / Telugu
export const SETTLED_SYNC_KEY = 'do_settled_sync';                 // { at, n } of the last settled-days refresh
export const LOG_SYNC_KEY = 'do_log_sync';                         // { through } — checklist log days this device has
export const CLOSING_REMINDER_LS_KEY = 'do_closing_reminder_dismissed';
export const ORDER_DAY_LS_KEY = 'do_orderday_dismissed';           // Tue/Fri shopping-day nudge (per-day)
export const VOICE_TIP_LS_KEY = 'do_voice_tip_dismissed';          // one-time "use the keyboard mic" hint (touch only)
export const DEBUG_LS_KEY = 'do_debug';                            // '1' → log Firestore listens (data/debug.js)

// ── Links & exports ──────────────────────────────────────────────────────────
export const EXPENSE_ROUTE = '/expense-calculator';   // History-tab link to the Expense Calculator
// Vercel deployment serving /api (Telegram). Mirrors Tickets/ticketConstants.js NOTIFY_API_BASE.
export const NOTIFY_API_BASE = 'https://my-app-ramakrishna5s-projects.vercel.app';
export const CSV_HEADERS = ['List for', 'Noted', 'List', 'Item', 'Qty', 'Status', 'Urgent', 'Added by', 'Handled by', 'Closed on', 'Closed by'];
export const AUDIT_CSV_HEADERS = [
  'Item', 'List', 'Times ordered', 'Pending', 'First ordered', 'Last ordered',
  'Days since', 'Avg days between', 'Stores', 'Last qty', 'Quantities',
];
